import { useSplitTestBranch } from '@api/lendingAPI/lapiHooks';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { Constants } from '@constants';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountIsMicroLoan } from '@helpers/Accounts/getAccountIsMicroLoan';
import { getAccountIsRBL } from '@helpers/Accounts/getAccountIsRBL';
import { getAccountIsRCF } from '@helpers/Accounts/getAccountIsRCF';
import { getAccountStatus } from '@helpers/Accounts/getAccountStatus';
import { getInLoanDrawdownOffer } from '@helpers/Accounts/getInLoanDrawdownOffer';
import { getTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus';
import { TTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { repeatsTopUpEligibilitySplitTestName } from '../../NewOutstandingView/NewOutstandingView.helpers';

type TUseGetRepaymentsPanelData = {
    isOverdue: boolean;
    canTopUpAfterRepayments: boolean;
    amountToRepayBeforeTopUp: number | undefined;
    isRBL: boolean;
    isMicroloan: boolean;
    isRCF: boolean;
    topUpStatus: TTopUpStatus;
    inFirstRepaymentTest: boolean;
    requestedCashAmount: number | undefined;
    requestedAmount: number | undefined;
};

export const useGetRepaymentsPanelData = (
    accountId: NonNullable<TAccount['account_id']>
): TUseGetRepaymentsPanelData => {
    const stateKey = useStateKey();

    const { data: account } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true
    });

    const { data: applicationResponse } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () => fetchGetApplication({ stateKey }),
        suspense: true
    });

    const { branch: splitTestBranch } = useSplitTestBranch({
        test: repeatsTopUpEligibilitySplitTestName,
        suspense: true
    });

    if (!account) {
        throw new Error();
    }

    const applicationData = applicationResponse?.data;
    const requestedProduct = applicationData?.requested_products?.[0];

    return {
        isOverdue: getAccountStatus(account) === 'overdue',
        canTopUpAfterRepayments: canTopUpAfterRepayments(account) || false,
        amountToRepayBeforeTopUp: amountToRepayBeforeTopUp(account),
        isRBL: getAccountIsRBL(account),
        isMicroloan: getAccountIsMicroLoan(account),
        isRCF: getAccountIsRCF(account),
        topUpStatus: getTopUpStatus(account, applicationData!),
        inFirstRepaymentTest: splitTestBranch === 'INCLUDE',
        requestedCashAmount:
            requestedProduct?.requested_cash_amount ?? undefined,
        requestedAmount: requestedProduct?.amount ?? undefined
    };
};

const canTopUpNow = (account: TAccount) => {
    return (
        isTopUpEligible(account) &&
        account.topup_eligibility?.amount_to_repay_before_topup === 0
    );
};

const isTopUpEligible = (account: TAccount) => {
    return account.topup_eligibility?.is_allowed;
};

const hasAvailableCash = (account: TAccount) => {
    const drawdownOffer = getInLoanDrawdownOffer(account);
    return drawdownOffer?.available_cash
        ? drawdownOffer?.available_cash >= Constants.MIN_DRAW_DOWN_AMOUNT
        : false;
};
const canTopUpAfterRepayments = (account: TAccount) => {
    return (
        !canTopUpNow(account) &&
        isTopUpEligible(account) &&
        !hasAvailableCash(account)
    );
};

const amountToRepayBeforeTopUp = (account: TAccount) => {
    return account.topup_eligibility?.amount_to_repay_before_topup;
};
